<script setup>
/**
 * FormControlContentContentTargetContentArea
 * Fetches available content areas based on a selected target page.
 * Uses manual async fetching to replace asyncComputed for Vue 3 compatibility.
 */
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import FormControlTypeSelect from '../form-control-type/FormControlTypeSelect.vue';

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number],
        default: ''
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const options = ref([]);

// --- Computed ---

/**
 * Specifically watch the target page field in currentData.
 * The content areas available depend on which page is selected.
 */
const targetPageId = computed(() => props.currentData.content__target_page);

// --- Methods ---

const fetchContentAreas = async () => {
    const pageId = targetPageId.value;

    if (!pageId) {
        options.value = [];
        return;
    }

    try {
        const contentAreas = await store.dispatch('pageTemplatesContentAreas/getPagesContentAreas', pageId);

        options.value = (contentAreas || []).map(area => ({
            value: area,
            text: area
        }));
    } catch (error) {
        console.error('Failed to fetch content areas for page:', pageId, error);
        options.value = [];
    }
};

// --- Lifecycle & Watchers ---

onMounted(() => {
    fetchContentAreas();
});

/**
 * If the target page changes, we must re-fetch the valid content areas.
 */
watch(targetPageId, () => {
    fetchContentAreas();
});
</script>

<template>
    <FormControlTypeSelect
        v-if="options.length"
        v-bind="$props"
        :options="options"
        v-on="$listeners"
    />
</template>